import { X } from 'lucide-react'

import { StackedBarChart, StackedBarChartBar, StackedBarChartTooltip } from '@/pages/ui/stacked-bar-chart'

import { Button } from '@/components/button/button'
import { SelectFilter } from '@/components/select-filter/select-filter'
import type { SelectFilterItem } from '@/components/select-filter/types'

import { cn } from '@/lib/utils'

import { CaseStatusBreakdownLegend } from './legend'
import useCaseStatusBreakdown from './use-case-status-breakdown'

const CHART_WIDTH = 642

interface Props {
  className?: string
  assignees: SelectFilterItem[]
  tags: SelectFilterItem[]
}

export const CaseStatusBreakdown: React.FC<Props> = ({ className, assignees, tags }) => {
  const {
    selectedAssignees,
    setSelectedAssignees,
    selectedTags,
    setSelectedTags,
    selectedActiveInactive,
    setSelectedActiveInactive,
    selectedTagCondition,
    setSelectedTagCondition,
    isFetchingCaseStatus,
    dataCaseStatus,
    onFilterReset,
    onImmigrationStatusClick,
    onEoirStatusClick,
  } = useCaseStatusBreakdown()

  return (
    <div className={cn('p-5 pb-[70px]', 'rounded-lg border border-border', 'shadow-sm', className)}>
      {/* FILTERS */}
      <div className={cn('mb-8', 'flex gap-2.5')}>
        <SelectFilter
          buttonText="Assignee"
          hideCount
          disabled={!assignees.length || isFetchingCaseStatus}
          values={selectedAssignees}
          items={assignees}
          onChangeValue={setSelectedAssignees}
        />
        <SelectFilter
          buttonText="Tag"
          hideCount
          showsFilterOption
          disabled={!tags.length || isFetchingCaseStatus}
          values={selectedTags}
          items={tags}
          filterOption={selectedTagCondition}
          onChangeFilterOption={setSelectedTagCondition}
          onChangeValue={setSelectedTags}
        />
        <SelectFilter
          buttonText="Active/Inactive"
          hideCount
          disabled={isFetchingCaseStatus}
          values={selectedActiveInactive}
          items={[
            { filterName: 'Active', filterValue: 'active', count: 0 },
            { filterName: 'Inactive', filterValue: 'inactive', count: 0 },
          ]}
          onChangeValue={setSelectedActiveInactive}
        />
        {!!(selectedAssignees.length || selectedTags.length || selectedActiveInactive.length) && (
          <Button className={cn('flex items-center gap-2 px-4')} variant="ghost" size="sm" onClick={onFilterReset}>
            Reset
            <X size={16} />
          </Button>
        )}
      </div>

      {/* HEADING */}
      <div className={cn('mb-[30px]', 'text-[18px] font-[600] leading-[28px]')}>Case Status Breakdown</div>

      {/* CHART */}
      <div
        className={cn('flex flex-col gap-[40px]')}
        style={{
          transition: 'opacity 120ms ease-out',
          opacity: isFetchingCaseStatus ? 0.7 : 1,
        }}
      >
        {!!dataCaseStatus.uscis?.length && (
          <ChartItemContainer
            title="USCIS"
            total={dataCaseStatus.totalUscis}
            chart={
              <StackedBarChart width={CHART_WIDTH} height={30}>
                <StackedBarChartBar data={dataCaseStatus.uscis} />
                <StackedBarChartTooltip data={dataCaseStatus.uscis} onClick={onImmigrationStatusClick} />
              </StackedBarChart>
            }
            legend={<CaseStatusBreakdownLegend width={CHART_WIDTH} data={dataCaseStatus.uscis} />}
          />
        )}
        {!!dataCaseStatus.nvc?.length && (
          <ChartItemContainer
            title="NVC"
            total={dataCaseStatus.totalNvc}
            chart={
              <StackedBarChart width={CHART_WIDTH} height={30}>
                <StackedBarChartBar data={dataCaseStatus.nvc} />
                <StackedBarChartTooltip data={dataCaseStatus.nvc} onClick={onImmigrationStatusClick} />
              </StackedBarChart>
            }
            legend={<CaseStatusBreakdownLegend width={CHART_WIDTH} data={dataCaseStatus.nvc} />}
          />
        )}
        {!!dataCaseStatus.dol?.length && (
          <ChartItemContainer
            title="DOL"
            total={dataCaseStatus.totalDol}
            chart={
              <StackedBarChart width={CHART_WIDTH} height={30}>
                <StackedBarChartBar data={dataCaseStatus.dol} />
                <StackedBarChartTooltip data={dataCaseStatus.dol} onClick={onImmigrationStatusClick} />
              </StackedBarChart>
            }
            legend={<CaseStatusBreakdownLegend width={CHART_WIDTH} data={dataCaseStatus.dol} />}
          />
        )}
        {!!dataCaseStatus.eoir?.length && (
          <ChartItemContainer
            title="EOIR"
            total={dataCaseStatus.totalEoir}
            chart={
              <StackedBarChart width={CHART_WIDTH} height={30}>
                <StackedBarChartBar data={dataCaseStatus.eoir} />
                <StackedBarChartTooltip data={dataCaseStatus.eoir} onClick={onEoirStatusClick} />
              </StackedBarChart>
            }
            legend={<CaseStatusBreakdownLegend width={CHART_WIDTH} data={dataCaseStatus.eoir} />}
          />
        )}
      </div>
    </div>
  )
}

interface ChartItemContainerProps {
  title: string
  total: number
  chart: React.ReactNode
  legend?: React.ReactNode
}

const ChartItemContainer: React.FC<ChartItemContainerProps> = ({ title, total, chart, legend }) => {
  return (
    <div className={cn('flex justify-center gap-[30px]')}>
      <div className={cn('w-[54px]', 'text-[16px] font-[500] leading-[24px]')}>{title}</div>
      <div className={cn('flex gap-5')}>
        <div className={cn('flex flex-col gap-5')}>
          <div className={cn('flex-1')}>{chart}</div>
          {legend}
        </div>
        <div className={cn('mt-[2px] w-[84px] text-left', 'text-[16px] font-[500]')}>Total: {total}</div>
      </div>
    </div>
  )
}

